import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import {
    ArrowRight,
    FileSearch,
    Calculator,
    FileSignature,
    ShieldCheck,
    Zap,
    Clock,
    Check,
    Scale,
    Sparkles,
    UploadCloud,
    Wand2,
    PackageCheck,
    Loader2,
    CheckCircle2,
    AlertCircle,
    Search,
    MapPin,
    Award,
    Users,
    MessageSquare,
    Calendar as CalendarIcon,
    BellRing,
    Bot,
    LineChart,
    LockKeyhole,
    PenLine,
    Database,
    Building2,
    ChevronDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { submitContact } from "@/lib/api";
import { trackEvent } from "@/lib/tracker";
import { toast } from "sonner";

const fadeUp = {
    hidden: { opacity: 0, y: 22 },
    show: (i = 0) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: i * 0.08 },
    }),
};

const stagger = {
    hidden: {},
    show: { transition: { staggerChildren: 0.08 } },
};

// Animated counter that fires once when scrolled into view.
function StatCounter({ value, suffix = "", duration = 1.6, decimals = 0 }) {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-50px" });
    const [n, setN] = useState(0);
    useEffect(() => {
        if (!inView) return;
        const start = performance.now();
        const animate = (t) => {
            const p = Math.min(1, (t - start) / (duration * 1000));
            const eased = 1 - Math.pow(1 - p, 3);
            setN(value * eased);
            if (p < 1) requestAnimationFrame(animate);
        };
        requestAnimationFrame(animate);
    }, [inView, value, duration]);
    return (
        <span ref={ref}>
            {decimals > 0
                ? n.toFixed(decimals).replace(".", ",")
                : Math.round(n).toLocaleString("es-ES")}
            {suffix}
        </span>
    );
}

export default function Landing() {
    const { user } = useAuth();
    const appHref = user ? "/app" : "/login";
    const ctaLabel = user ? "Ir a mi panel" : "Solicitar demo";
    const ctaTarget = user ? appHref : "#precios";

    const heroRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: heroRef,
        offset: ["start start", "end start"],
    });
    const blobY = useTransform(scrollYProgress, [0, 1], [0, -120]);
    const blobOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.2]);

    useEffect(() => {
        document.title =
            "LicitaIA · IA para licitaciones públicas (PLCSP, DEUC, AutoFirma) | España";
        // Make sure the description matches the index.html one (in case React
        // mounted after a different page swapped it).
        const desc = document.querySelector('meta[name="description"]');
        if (desc) {
            desc.setAttribute(
                "content",
                "Gana más licitaciones públicas con IA. LicitaIA analiza pliegos en 3 min, calcula el escandallo, rellena el DEUC y los anexos LCSP, firma con AutoFirma y prepara el ZIP para la PLCSP. 855.000 licitaciones indexadas.",
            );
        }
    }, []);

    // Conversion-funnel tracking — fire once per session when the visitor
    // either uses any "Solicitar demo" anchor (lands on #precios) or scrolls
    // into the contact form section. Funnel step 2 of 4.
    useEffect(() => {
        if (user) return; // logged-in users are not part of the demo funnel
        let fired = false;
        const fire = () => {
            if (fired) return;
            fired = true;
            trackEvent("demo");
        };
        const onHash = () => {
            if (window.location.hash === "#precios") fire();
        };
        if (window.location.hash === "#precios") fire();
        window.addEventListener("hashchange", onHash);
        // Also catch direct clicks on anchors pointing to #precios so we fire
        // even when there is no actual hashchange (already on #precios).
        const onDocClick = (ev) => {
            const a = ev.target?.closest?.('a[href="#precios"]');
            if (a) fire();
        };
        document.addEventListener("click", onDocClick);
        return () => {
            window.removeEventListener("hashchange", onHash);
            document.removeEventListener("click", onDocClick);
        };
    }, [user]);

    return (
        <div className="min-h-screen bg-[#f6f8ff] text-ink-950" data-testid="landing-page">
            {/* NAV */}
            <header className="sticky top-0 z-30">
                <div className="max-w-7xl mx-auto px-6 mt-4">
                    <div className="glass shadow-pop rounded-2xl h-16 flex items-center justify-between px-5">
                        <Link to="/" className="flex items-center gap-2.5" data-testid="logo-link">
                            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-brand-600 to-cyan-400 text-white flex items-center justify-center shadow-pop">
                                <ShieldCheck size={18} strokeWidth={2.4} />
                            </div>
                            <span className="font-display text-xl font-extrabold tracking-tight">
                                LicitaIA
                            </span>
                        </Link>
                        <nav className="hidden md:flex items-center gap-7 text-sm font-semibold text-ink-500">
                            <a href="#producto" className="hover:text-brand-600 transition-colors">
                                Producto
                            </a>
                            <a href="#plcsp" className="hover:text-brand-600 transition-colors">
                                Buscador PLCSP
                            </a>
                            <a href="#equipos" className="hover:text-brand-600 transition-colors">
                                Equipos
                            </a>
                            <a href="#proceso" className="hover:text-brand-600 transition-colors">
                                Proceso
                            </a>
                            <a href="#faq" className="hover:text-brand-600 transition-colors">
                                FAQ
                            </a>
                            <a href="#precios" className="hover:text-brand-600 transition-colors">
                                Precios
                            </a>
                        </nav>
                        <Link to={appHref}>
                            <Button
                                className="rounded-full bg-brand-600 hover:bg-brand-700 text-white px-5 h-10 shadow-pop hover:scale-[1.03] transition-all"
                                data-testid="cta-enter-app"
                            >
                                {user ? "Ir a mi panel" : "Entrar a la app"}
                                <ArrowRight size={15} className="ml-1.5" />
                            </Button>
                        </Link>
                    </div>
                </div>
            </header>

            {/* HERO */}
            <section ref={heroRef} className="relative overflow-hidden">
                <div className="absolute inset-0 bg-grid-brand opacity-60" />
                <motion.div
                    style={{ y: blobY, opacity: blobOpacity }}
                    className="pointer-events-none absolute -top-40 -right-40 w-[640px] h-[640px] rounded-full bg-gradient-to-br from-brand-600/30 via-cyan-400/30 to-sky-400/20 blur-3xl"
                />
                <motion.div
                    style={{ y: blobY }}
                    className="pointer-events-none absolute top-40 -left-40 w-[520px] h-[520px] rounded-full bg-gradient-to-br from-cyan-400/30 to-brand-600/20 blur-3xl"
                />

                <div className="max-w-7xl mx-auto px-6 pt-24 pb-32 md:pt-32 md:pb-40 relative">
                    <motion.div
                        initial="hidden"
                        animate="show"
                        variants={stagger}
                        className="max-w-4xl"
                    >
                        <motion.div
                            variants={fadeUp}
                            className="inline-flex items-center gap-2 bg-white border border-brand-100 px-3.5 py-1.5 rounded-full mb-7 shadow-pop"
                        >
                            <span className="relative flex w-2 h-2">
                                <span className="absolute inset-0 rounded-full bg-brand-600 pulse-dot" />
                                <span className="relative rounded-full w-2 h-2 bg-brand-600" />
                            </span>
                            <span className="text-xs font-bold uppercase tracking-[0.18em] text-brand-700">
                                IA Claude Sonnet 4.5 · 855.000 licitaciones indexadas
                            </span>
                        </motion.div>

                        <motion.h1
                            variants={fadeUp}
                            className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[0.95] mb-7"
                        >
                            Gana más{" "}
                            <span className="relative inline-block">
                                <span className="relative z-10 bg-gradient-to-r from-brand-600 via-brand-500 to-cyan-500 bg-clip-text text-transparent">
                                    licitaciones públicas
                                </span>
                                <svg
                                    className="absolute -bottom-2 left-0 w-full"
                                    viewBox="0 0 300 12"
                                    fill="none"
                                >
                                    <path
                                        d="M2 8 Q 75 2, 150 6 T 298 5"
                                        stroke="url(#g)"
                                        strokeWidth="3"
                                        strokeLinecap="round"
                                        fill="none"
                                    />
                                    <defs>
                                        <linearGradient id="g" x1="0" x2="1">
                                            <stop offset="0" stopColor="#0055ff" />
                                            <stop offset="1" stopColor="#22d3ee" />
                                        </linearGradient>
                                    </defs>
                                </svg>
                            </span>
                            <br />
                            con IA, en horas.
                        </motion.h1>

                        <motion.p
                            variants={fadeUp}
                            className="text-xl md:text-2xl text-ink-500 max-w-2xl mb-10 leading-relaxed font-medium"
                        >
                            LicitaIA analiza tus pliegos, calcula el escandallo, rellena el
                            DEUC y los anexos LCSP, firma con AutoFirma y prepara el ZIP listo
                            para la PLCSP. Buscador semántico con 855.000 licitaciones públicas
                            y filtros por municipio, provincia y CCAA.
                        </motion.p>

                        <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-3">
                            {user ? (
                                <Link to={appHref}>
                                    <Button
                                        size="lg"
                                        className="rounded-full bg-brand-600 hover:bg-brand-700 text-white px-8 h-14 text-base font-bold shadow-pop-lg hover:scale-[1.03] hover:-translate-y-0.5 transition-all duration-200"
                                        data-testid="hero-cta-primary"
                                    >
                                        <Sparkles size={18} className="mr-2" />
                                        {ctaLabel}
                                        <ArrowRight size={17} className="ml-2" />
                                    </Button>
                                </Link>
                            ) : (
                                <a href={ctaTarget}>
                                    <Button
                                        size="lg"
                                        className="rounded-full bg-brand-600 hover:bg-brand-700 text-white px-8 h-14 text-base font-bold shadow-pop-lg hover:scale-[1.03] hover:-translate-y-0.5 transition-all duration-200"
                                        data-testid="hero-cta-primary"
                                    >
                                        <Sparkles size={18} className="mr-2" />
                                        {ctaLabel}
                                        <ArrowRight size={17} className="ml-2" />
                                    </Button>
                                </a>
                            )}
                            <a href="#proceso">
                                <Button
                                    size="lg"
                                    variant="outline"
                                    className="rounded-full border-2 border-ink-100 bg-white hover:border-brand-600 hover:text-brand-600 text-ink-700 px-8 h-14 text-base font-bold w-full transition-all"
                                    data-testid="hero-cta-secondary"
                                >
                                    Ver cómo funciona
                                </Button>
                            </a>
                        </motion.div>

                        <motion.div
                            variants={fadeUp}
                            className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-3xl"
                        >
                            {[
                                { k: <StatCounter value={855000} suffix="" />, v: "licitaciones indexadas" },
                                { k: <><StatCounter value={3} suffix=" min" /></>, v: "análisis IA medio" },
                                { k: <><StatCounter value={87} suffix=" %" /></>, v: "menos tiempo por oferta" },
                                { k: "PAdES", v: "firma eIDAS AutoFirma" },
                            ].map((s, i) => (
                                <div
                                    key={i}
                                    className="bg-white/60 backdrop-blur rounded-2xl px-4 py-3 border border-white"
                                >
                                    <div className="font-display text-2xl font-extrabold text-brand-700">
                                        {s.k}
                                    </div>
                                    <div className="text-xs uppercase tracking-[0.15em] text-ink-400 font-bold mt-1">
                                        {s.v}
                                    </div>
                                </div>
                            ))}
                        </motion.div>
                    </motion.div>

                    {/* Floating product mockup card */}
                    <motion.div
                        initial={{ opacity: 0, y: 60, rotate: -2 }}
                        animate={{ opacity: 1, y: 0, rotate: -2 }}
                        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.4 }}
                        className="hidden lg:block absolute right-8 top-32 w-[380px] float-slow"
                    >
                        <div className="glass shadow-pop-lg rounded-3xl p-5 border border-white">
                            <div className="flex items-center gap-2 mb-3">
                                <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
                                <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                                <div className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
                                <div className="ml-auto text-[10px] font-mono text-ink-400">
                                    licitacion.pdf
                                </div>
                            </div>
                            <div className="rounded-2xl bg-gradient-to-br from-brand-600 to-cyan-500 p-5 text-white relative overflow-hidden">
                                <div className="absolute inset-0 bg-grid-brand opacity-30" />
                                <div className="relative">
                                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-80 mb-2">
                                        Análisis IA · 3 min
                                    </div>
                                    <div className="font-display text-xl font-extrabold leading-tight mb-3">
                                        Suministro material sanitario · Lote 2
                                    </div>
                                    <div className="flex gap-2 flex-wrap">
                                        {["CPV 33141000", "92 puntos", "DEUC OK"].map((t) => (
                                            <span
                                                key={t}
                                                className="text-[10px] font-bold uppercase tracking-wider bg-white/20 backdrop-blur px-2 py-1 rounded-full"
                                            >
                                                {t}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div className="mt-3 space-y-2">
                                {[
                                    ["Presupuesto base", "248.500 €"],
                                    ["Margen estimado", "14,2 %"],
                                    ["Plazo presentación", "12 días"],
                                ].map(([k, v]) => (
                                    <div
                                        key={k}
                                        className="flex items-center justify-between text-sm"
                                    >
                                        <span className="text-ink-500 font-medium">{k}</span>
                                        <span className="font-mono font-semibold text-ink-900">
                                            {v}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* FEATURES */}
            <section id="producto" className="max-w-7xl mx-auto px-6 py-24 lg:py-32">
                <motion.div
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, margin: "-100px" }}
                    variants={stagger}
                    className="max-w-2xl mb-16"
                >
                    <motion.div
                        variants={fadeUp}
                        className="text-xs font-bold uppercase tracking-[0.2em] text-brand-600 mb-4"
                    >
                        — Capacidades
                    </motion.div>
                    <motion.h2
                        variants={fadeUp}
                        className="font-display text-4xl md:text-5xl tracking-tight font-black mb-5 leading-[1.05]"
                    >
                        Todo lo que necesitas para{" "}
                        <span className="text-brand-600">preparar una oferta</span>.
                    </motion.h2>
                    <motion.p
                        variants={fadeUp}
                        className="text-lg text-ink-500 leading-relaxed font-medium"
                    >
                        Sube el pliego en PDF y nuestra IA hace el trabajo pesado: lectura,
                        extracción, cálculo y redacción.
                    </motion.p>
                </motion.div>

                <motion.div
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, margin: "-100px" }}
                    variants={stagger}
                    className="grid md:grid-cols-2 lg:grid-cols-3 gap-5"
                >
                    {[
                        {
                            icon: FileSearch,
                            title: "Análisis IA de pliegos",
                            body: "Lee PCAP, PPT y anexos en minutos. Extrae objeto, presupuesto, criterios de adjudicación, subrogación de personal y bill of quantities.",
                            color: "from-brand-600 to-cyan-500",
                        },
                        {
                            icon: Calculator,
                            title: "Escandallo editable",
                            body: "Personal, materiales, subcontrataciones, gastos generales y BI. Cálculo en vivo con margen objetivo y export a Excel.",
                            color: "from-cyan-500 to-sky-400",
                        },
                        {
                            icon: FileSignature,
                            title: "Anexos LCSP automáticos",
                            body: "DEUC, declaración responsable Art. 65, anexos de solvencia técnica y económica, declaración UTE — rellenados por IA en PDF y DOCX.",
                            color: "from-sky-400 to-brand-500",
                        },
                        {
                            icon: PenLine,
                            title: "AutoFirma · PAdES eIDAS",
                            body: "Firma electrónica cualificada de toda la cadena de anexos con AutoFirma del Gobierno de España. Bulk-signing en un click.",
                            color: "from-brand-500 to-brand-600",
                        },
                        {
                            icon: Search,
                            title: "Buscador PLCSP semántico",
                            body: "855.000 licitaciones públicas indexadas (12 meses). Búsqueda semántica con IA, filtros por municipio, provincia y comunidad autónoma.",
                            color: "from-brand-600 to-brand-800",
                        },
                        {
                            icon: Award,
                            title: "Inteligencia competitiva",
                            body: "Sigue adjudicaciones de tus rivales por CIF. Alertas automáticas, histórico de contratos y análisis de patrones de oferta.",
                            color: "from-cyan-400 to-cyan-600",
                        },
                        {
                            icon: Scale,
                            title: "Solvencia técnica & económica",
                            body: "Genera cuadros de solvencia LCSP Art. 87-90 desde tus contratos y balances. Matching automático con requisitos del pliego.",
                            color: "from-brand-600 to-cyan-500",
                        },
                        {
                            icon: MessageSquare,
                            title: "Comunicaciones inteligentes",
                            body: "Centraliza requerimientos del órgano de contratación. La IA extrae fechas límite del PDF y las añade a tu calendario.",
                            color: "from-cyan-500 to-sky-400",
                        },
                        {
                            icon: Users,
                            title: "Equipos multi-usuario",
                            body: "Cuentas de empresa con roles. Restringe módulos por empleado (licitaciones, calendario, analíticas) y colabora sin duplicar trabajo.",
                            color: "from-sky-400 to-brand-500",
                        },
                    ].map((f, i) => (
                        <motion.div
                            key={f.title}
                            variants={fadeUp}
                            custom={i}
                            whileHover={{ y: -6 }}
                            transition={{ duration: 0.3 }}
                            className="group relative bg-white rounded-3xl p-7 border border-ink-100 hover:border-brand-200 shadow-pop hover:shadow-pop-lg transition-all"
                        >
                            <div
                                className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${f.color} text-white flex items-center justify-center mb-5 shadow-pop group-hover:scale-110 transition-transform`}
                            >
                                <f.icon size={20} strokeWidth={2.2} />
                            </div>
                            <div className="font-display text-xl font-bold mb-2 text-ink-950">
                                {f.title}
                            </div>
                            <div className="text-sm text-ink-500 leading-relaxed">{f.body}</div>
                        </motion.div>
                    ))}
                </motion.div>
            </section>

            {/* PLCSP SEARCH SHOWCASE */}
            <section
                id="plcsp"
                className="relative overflow-hidden border-y border-ink-100 bg-gradient-to-br from-brand-50/40 via-white to-cyan-50/40"
            >
                <div className="max-w-7xl mx-auto px-6 py-24 lg:py-32 grid lg:grid-cols-2 gap-16 items-center">
                    <motion.div
                        initial="hidden"
                        whileInView="show"
                        viewport={{ once: true, margin: "-100px" }}
                        variants={stagger}
                    >
                        <motion.div
                            variants={fadeUp}
                            className="text-xs font-bold uppercase tracking-[0.2em] text-brand-600 mb-4"
                        >
                            — Buscador PLCSP semántico
                        </motion.div>
                        <motion.h2
                            variants={fadeUp}
                            className="font-display text-4xl md:text-5xl tracking-tight font-black mb-5 leading-[1.05]"
                        >
                            <StatCounter value={855000} /> licitaciones públicas, a un{" "}
                            <span className="text-brand-600">click</span>.
                        </motion.h2>
                        <motion.p
                            variants={fadeUp}
                            className="text-lg text-ink-500 leading-relaxed font-medium mb-6"
                        >
                            Cache propia de 12 meses de la PLCSP, plataformas autonómicas,
                            locales y contratos menores. La IA expande tu búsqueda en sinónimos
                            y códigos CPV relevantes para que no se te escape ningún anuncio.
                        </motion.p>
                        <motion.div variants={fadeUp} className="grid grid-cols-2 gap-3 mb-7">
                            {[
                                { icon: Database, t: "855k licitaciones", d: "indexadas 12 meses" },
                                { icon: Bot, t: "Búsqueda semántica", d: "IA expande términos + CPV" },
                                { icon: MapPin, t: "Filtros geográficos", d: "Municipio · Provincia · CCAA" },
                                { icon: BellRing, t: "Alertas tiempo real", d: "Email cuando salga match" },
                            ].map((f) => (
                                <div
                                    key={f.t}
                                    className="bg-white rounded-2xl p-4 border border-ink-100 shadow-pop"
                                >
                                    <f.icon size={18} className="text-brand-600 mb-2" />
                                    <div className="font-bold text-ink-950 text-sm">{f.t}</div>
                                    <div className="text-xs text-ink-500 mt-0.5">{f.d}</div>
                                </div>
                            ))}
                        </motion.div>
                        <motion.div variants={fadeUp}>
                            <Link to={appHref}>
                                <Button
                                    size="lg"
                                    className="rounded-full bg-brand-600 hover:bg-brand-700 text-white px-7 h-12 font-bold shadow-pop hover:scale-[1.03] transition-all"
                                    data-testid="plcsp-cta"
                                >
                                    <Search size={16} className="mr-2" />
                                    Probar buscador PLCSP
                                    <ArrowRight size={16} className="ml-2" />
                                </Button>
                            </Link>
                        </motion.div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, x: 40 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.7 }}
                        className="relative"
                    >
                        <div className="glass shadow-pop-lg rounded-3xl p-6 border border-white">
                            <div className="flex items-center gap-2 mb-4 px-2">
                                <Search size={16} className="text-brand-600" />
                                <div className="flex-1 bg-brand-50 rounded-xl px-3 py-2 text-sm font-mono text-ink-700">
                                    suministro mobiliario centros educativos andalucía
                                </div>
                                <div className="bg-brand-600 text-white text-xs font-bold px-3 py-2 rounded-xl">
                                    Buscar
                                </div>
                            </div>
                            <div className="space-y-3">
                                {[
                                    {
                                        org: "Junta de Andalucía · Consejería de Educación",
                                        title: "Suministro mobiliario IES — Sevilla",
                                        cpv: "39160000",
                                        budget: "248.500 €",
                                        days: 12,
                                        match: 96,
                                    },
                                    {
                                        org: "Ayuntamiento de Málaga",
                                        title: "Equipamiento aulas formación profesional",
                                        cpv: "39162110",
                                        budget: "94.800 €",
                                        days: 8,
                                        match: 91,
                                    },
                                    {
                                        org: "Diputación de Córdoba",
                                        title: "Mobiliario escolar colegios rurales",
                                        cpv: "39160000",
                                        budget: "132.000 €",
                                        days: 21,
                                        match: 87,
                                    },
                                ].map((r) => (
                                    <motion.div
                                        key={r.title}
                                        whileHover={{ y: -2 }}
                                        transition={{ duration: 0.2 }}
                                        className="bg-white rounded-2xl p-4 border border-ink-100 hover:border-brand-300 shadow-pop transition-all"
                                    >
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="flex-1 min-w-0">
                                                <div className="text-[10px] font-bold uppercase tracking-wider text-ink-400 truncate">
                                                    {r.org}
                                                </div>
                                                <div className="font-bold text-ink-950 text-sm mt-0.5 truncate">
                                                    {r.title}
                                                </div>
                                                <div className="flex gap-2 mt-2 flex-wrap">
                                                    <span className="text-[10px] font-mono bg-brand-50 text-brand-700 px-2 py-0.5 rounded font-bold">
                                                        CPV {r.cpv}
                                                    </span>
                                                    <span className="text-[10px] font-mono bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded font-bold">
                                                        {r.budget}
                                                    </span>
                                                    <span className="text-[10px] font-mono bg-amber-50 text-amber-700 px-2 py-0.5 rounded font-bold">
                                                        {r.days} días
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="shrink-0">
                                                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-brand-600 to-cyan-500 text-white flex items-center justify-center font-mono text-xs font-extrabold shadow-pop">
                                                    {r.match}%
                                                </div>
                                                <div className="text-[9px] uppercase tracking-wider text-ink-400 font-bold text-center mt-1">
                                                    Match
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* TEAM / MULTIUSER */}
            <section id="equipos" className="max-w-7xl mx-auto px-6 py-24 lg:py-32">
                <motion.div
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, margin: "-100px" }}
                    variants={stagger}
                    className="max-w-3xl mb-14"
                >
                    <motion.div
                        variants={fadeUp}
                        className="text-xs font-bold uppercase tracking-[0.2em] text-brand-600 mb-4"
                    >
                        — Cuentas de empresa
                    </motion.div>
                    <motion.h2
                        variants={fadeUp}
                        className="font-display text-4xl md:text-5xl tracking-tight font-black mb-5 leading-[1.05]"
                    >
                        Tu oficina técnica,{" "}
                        <span className="text-brand-600">en una sola plataforma</span>.
                    </motion.h2>
                    <motion.p
                        variants={fadeUp}
                        className="text-lg text-ink-500 leading-relaxed font-medium"
                    >
                        Invita a todo tu equipo bajo una cuenta de empresa. Asigna roles, da
                        permisos por módulo y comparte licitaciones, perfil de solvencia y
                        documentación corporativa sin duplicar nada.
                    </motion.p>
                </motion.div>

                <div className="grid md:grid-cols-3 gap-5">
                    {[
                        {
                            icon: Building2,
                            title: "Una empresa, muchos usuarios",
                            body: "El admin crea miembros con email y contraseña. Todos los datos —tenders, comunicaciones, documentos, solvencia— se comparten dentro de la empresa.",
                        },
                        {
                            icon: LockKeyhole,
                            title: "Permisos por módulo",
                            body: "Restringe acceso a Calendario, Analíticas, Competidores o Solvencia. Los comerciales ven licitaciones; los técnicos solo solvencia.",
                        },
                        {
                            icon: LineChart,
                            title: "Visión global del pipeline",
                            body: "Cuadro de mando con licitaciones por estado, ratio de éxito, plazos críticos y mapa de calor de actividad por miembro.",
                        },
                    ].map((c, i) => (
                        <motion.div
                            key={c.title}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 0.5, delay: i * 0.08 }}
                            whileHover={{ y: -4 }}
                            className="bg-white rounded-3xl p-7 border border-ink-100 hover:border-brand-300 shadow-pop hover:shadow-pop-lg transition-all"
                        >
                            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-brand-600 to-cyan-500 text-white flex items-center justify-center mb-5 shadow-pop">
                                <c.icon size={20} strokeWidth={2.2} />
                            </div>
                            <div className="font-display text-xl font-bold mb-2 text-ink-950">
                                {c.title}
                            </div>
                            <div className="text-sm text-ink-500 leading-relaxed">{c.body}</div>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* MARQUEE — keywords reel for SEO + visual interest */}
            <section
                aria-hidden="true"
                className="relative overflow-hidden border-y border-ink-100 bg-white py-8"
            >
                <div className="flex gap-12 marquee-track whitespace-nowrap text-ink-300 font-display text-3xl md:text-4xl font-black tracking-tight">
                    {Array.from({ length: 2 }).map((_, idx) => (
                        <div key={idx} className="flex gap-12">
                            <span>PLCSP</span>
                            <span className="text-brand-300">·</span>
                            <span>DEUC</span>
                            <span className="text-brand-300">·</span>
                            <span>Ley 9/2017</span>
                            <span className="text-brand-300">·</span>
                            <span>AutoFirma</span>
                            <span className="text-brand-300">·</span>
                            <span>PAdES eIDAS</span>
                            <span className="text-brand-300">·</span>
                            <span>Escandallo</span>
                            <span className="text-brand-300">·</span>
                            <span>CPV</span>
                            <span className="text-brand-300">·</span>
                            <span>Solvencia técnica</span>
                            <span className="text-brand-300">·</span>
                            <span>Solvencia económica</span>
                            <span className="text-brand-300">·</span>
                            <span>Anexos LCSP</span>
                            <span className="text-brand-300">·</span>
                            <span>UTE</span>
                            <span className="text-brand-300">·</span>
                            <span>Subrogación de personal</span>
                            <span className="text-brand-300">·</span>
                        </div>
                    ))}
                </div>
            </section>

            {/* PROCESS */}
            <section
                id="proceso"
                className="relative bg-gradient-to-b from-white to-brand-50/40 border-y border-ink-100"
            >
                <div className="max-w-7xl mx-auto px-6 py-24 lg:py-32">
                    <div className="grid lg:grid-cols-2 gap-16 items-start">
                        <motion.div
                            initial="hidden"
                            whileInView="show"
                            viewport={{ once: true }}
                            variants={stagger}
                        >
                            <motion.div
                                variants={fadeUp}
                                className="text-xs font-bold uppercase tracking-[0.2em] text-brand-600 mb-4"
                            >
                                — Proceso
                            </motion.div>
                            <motion.h2
                                variants={fadeUp}
                                className="font-display text-4xl md:text-5xl tracking-tight font-black mb-6 leading-[1.05]"
                            >
                                De pliego a oferta firmada{" "}
                                <span className="text-brand-600">en cuatro pasos</span>.
                            </motion.h2>
                            <motion.p
                                variants={fadeUp}
                                className="text-lg text-ink-500 leading-relaxed font-medium mb-8"
                            >
                                Lo que antes llevaba dos semanas de oficina técnica ahora se
                                prepara en una tarde. La IA no sustituye a tu equipo — le quita
                                el trabajo repetitivo para que se concentre en ganar.
                            </motion.p>
                            <motion.div variants={fadeUp}>
                                <a href={user ? appHref : "#precios"}>
                                    <Button
                                        size="lg"
                                        className="rounded-full bg-brand-600 hover:bg-brand-700 text-white px-8 h-12 font-bold shadow-pop hover:scale-[1.03] transition-all"
                                        data-testid="process-cta"
                                    >
                                        {user ? "Ir a mi panel" : "Solicitar demo"}
                                        <ArrowRight size={16} className="ml-2" />
                                    </Button>
                                </a>
                            </motion.div>
                        </motion.div>

                        <motion.ol
                            initial="hidden"
                            whileInView="show"
                            viewport={{ once: true, margin: "-100px" }}
                            variants={stagger}
                            className="space-y-4"
                        >
                            {[
                                {
                                    icon: Plus,
                                    n: "01",
                                    t: "Crea la licitación",
                                    d: "Indica título, órgano de contratación y fecha límite. Importas también desde la PLCSP.",
                                },
                                {
                                    icon: UploadCloud,
                                    n: "02",
                                    t: "Sube el pliego en PDF",
                                    d: "PCAP, PPT o consolidado. Multi-archivo soportado en el mismo drop.",
                                },
                                {
                                    icon: Wand2,
                                    n: "03",
                                    t: "Revisa el análisis IA",
                                    d: "Resumen ejecutivo, requisitos críticos y escandallo inicial — todo editable.",
                                },
                                {
                                    icon: PackageCheck,
                                    n: "04",
                                    t: "Genera y descarga",
                                    d: "Anexos firmados y paquete ZIP listo para la Plataforma del Estado.",
                                },
                            ].map((s) => (
                                <motion.li
                                    key={s.n}
                                    variants={fadeUp}
                                    whileHover={{ x: 6 }}
                                    transition={{ duration: 0.3 }}
                                    className="group flex gap-5 bg-white rounded-2xl p-6 border border-ink-100 hover:border-brand-300 shadow-pop hover:shadow-pop-lg transition-all"
                                >
                                    <div className="shrink-0">
                                        <div className="w-12 h-12 rounded-xl bg-brand-50 group-hover:bg-brand-600 group-hover:text-white text-brand-600 flex items-center justify-center transition-all">
                                            <s.icon size={20} strokeWidth={2.2} />
                                        </div>
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-baseline gap-3 mb-1">
                                            <span className="font-mono text-xs font-bold text-brand-600">
                                                {s.n}
                                            </span>
                                            <span className="font-display text-lg font-bold text-ink-950">
                                                {s.t}
                                            </span>
                                        </div>
                                        <div className="text-sm text-ink-500 leading-relaxed">
                                            {s.d}
                                        </div>
                                    </div>
                                </motion.li>
                            ))}
                        </motion.ol>
                    </div>
                </div>
            </section>

            {/* FAQ — also matches the JSON-LD FAQPage in index.html */}
            <section id="faq" className="max-w-5xl mx-auto px-6 py-24 lg:py-32">
                <motion.div
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, margin: "-100px" }}
                    variants={stagger}
                    className="text-center mb-14"
                >
                    <motion.div
                        variants={fadeUp}
                        className="text-xs font-bold uppercase tracking-[0.2em] text-brand-600 mb-4"
                    >
                        — Preguntas frecuentes
                    </motion.div>
                    <motion.h2
                        variants={fadeUp}
                        className="font-display text-4xl md:text-5xl tracking-tight font-black mb-4 leading-[1.05]"
                    >
                        Todo lo que necesitas saber sobre{" "}
                        <span className="text-brand-600">LicitaIA</span>.
                    </motion.h2>
                </motion.div>

                <div className="space-y-3">
                    {[
                        {
                            q: "¿Qué es LicitaIA y para qué sirve?",
                            a: "LicitaIA es una plataforma de inteligencia artificial para empresas que se presentan a licitaciones públicas en España. La IA analiza los pliegos (PCAP, PPT) en menos de 3 minutos, calcula el escandallo de costes, genera el DEUC y los anexos exigidos por la LCSP, y prepara el paquete ZIP listo para subir a la PLCSP.",
                        },
                        {
                            q: "¿Cumple con la Ley 9/2017 de Contratos del Sector Público?",
                            a: "Sí. LicitaIA aplica las exigencias de la Ley 9/2017 LCSP y las directivas europeas. Genera el DEUC oficial, los anexos de solvencia técnica y económica (Art. 87-90 LCSP) y las declaraciones responsables del Art. 65 LCSP. La firma electrónica de los anexos cumple el formato PAdES eIDAS mediante AutoFirma del Gobierno de España.",
                        },
                        {
                            q: "¿Cómo busca licitaciones en la PLCSP?",
                            a: "LicitaIA mantiene un índice de 855.000 licitaciones públicas históricas de los últimos 12 meses (PLACSP estatal, plataformas autonómicas, locales y contratos menores). La búsqueda semántica con IA expande tu término en sinónimos y códigos CPV relevantes. Puedes filtrar por municipio, provincia, comunidad autónoma, presupuesto y plazo abierto.",
                        },
                        {
                            q: "¿Cuánto tiempo se tarda en preparar una oferta?",
                            a: "Lo que antes llevaba dos semanas de oficina técnica ahora se prepara en una tarde. El análisis IA del pliego tarda unos 3 minutos. La generación del escandallo, los anexos y el DEUC tarda otros 2-3 minutos. La firma electrónica con AutoFirma de toda la cadena de anexos se completa en menos de un minuto.",
                        },
                        {
                            q: "¿Puedo trabajar en equipo en la misma cuenta?",
                            a: "Sí. LicitaIA soporta cuentas multi-usuario por empresa. El administrador invita a los empleados, les asigna rol (administrador o miembro) y restringe el acceso a módulos concretos (licitaciones, calendario, solvencia, analíticas, etc.). Todos los datos se comparten dentro de la empresa para colaborar sin duplicar trabajo.",
                        },
                        {
                            q: "¿Cuánto cuesta LicitaIA?",
                            a: "Ofrecemos una demo gratuita personalizada con tus pliegos en menos de 24 horas tras solicitarla. Los planes se ajustan al volumen de licitaciones que presenta tu empresa al mes. Solicita una demo para recibir un presupuesto sin compromiso.",
                        },
                    ].map((item, i) => (
                        <FaqItem key={i} q={item.q} a={item.a} />
                    ))}
                </div>
            </section>

            {/* SEO content block — long-form for Google indexing */}
            <section className="bg-gradient-to-b from-white to-brand-50/40 border-t border-ink-100">
                <div className="max-w-5xl mx-auto px-6 py-20 prose-licitaia">
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand-600 mb-4">
                        — Software para licitaciones públicas en España
                    </div>
                    <h2 className="font-display text-3xl md:text-4xl tracking-tight font-black mb-6 leading-[1.1] text-ink-950">
                        Inteligencia artificial aplicada a la contratación pública española
                    </h2>
                    <div className="space-y-5 text-ink-600 leading-relaxed font-medium text-base md:text-lg">
                        <p>
                            La contratación pública en España mueve más de{" "}
                            <strong className="text-ink-950">220.000 millones de euros</strong>{" "}
                            anuales y se rige por la <strong>Ley 9/2017 de Contratos del Sector
                            Público (LCSP)</strong>, transposición de las directivas europeas
                            2014/23/UE, 2014/24/UE y 2014/25/UE. Cada año se publican en la{" "}
                            <strong>Plataforma de Contratación del Sector Público (PLCSP)</strong>{" "}
                            cientos de miles de anuncios de licitaciones de obras, suministros
                            y servicios, distribuidos entre la Administración General del
                            Estado, las comunidades autónomas, las entidades locales y las
                            sociedades mercantiles del sector público.
                        </p>
                        <p>
                            Para una pyme presentar oferta a un solo pliego puede suponer{" "}
                            <strong>40 a 80 horas de trabajo</strong> entre la lectura del
                            PCAP y el PPT, la elaboración del escandallo de costes, la
                            cumplimentación del DEUC (Documento Europeo Único de Contratación),
                            la preparación de los anexos de solvencia técnica y económica (
                            <strong>Art. 87 a 90 LCSP</strong>), la declaración responsable del{" "}
                            <strong>Art. 65 LCSP</strong>, y la firma electrónica de toda la
                            documentación con <strong>AutoFirma</strong>.
                        </p>
                        <h3 className="font-display text-xl md:text-2xl font-bold text-ink-950 mt-8 mb-3">
                            Cómo LicitaIA reduce ese tiempo a una tarde
                        </h3>
                        <p>
                            LicitaIA utiliza modelos de IA generativa de última generación
                            (Claude Sonnet 4.5 con visión) para leer los pliegos de la misma
                            forma que lo haría un jefe de proyecto experimentado: identifica
                            objeto del contrato, presupuesto base de licitación, valor estimado,
                            criterios de adjudicación, plazos, garantías, código CPV, lotes,
                            subrogación de personal y exigencias de solvencia. A partir de ahí
                            propone un primer escandallo de costes editable, redacta los anexos
                            del expediente y firma toda la cadena con PAdES eIDAS para subir el
                            ZIP final a la PLCSP.
                        </p>
                        <h3 className="font-display text-xl md:text-2xl font-bold text-ink-950 mt-8 mb-3">
                            Buscador semántico con 855.000 licitaciones públicas
                        </h3>
                        <p>
                            Además del análisis de pliegos, LicitaIA incluye un buscador
                            semántico sobre un índice propio de <strong>855.000 licitaciones
                            públicas</strong> de los últimos 12 meses. Combina datos de la
                            <strong> PLACSP</strong> estatal con plataformas autonómicas
                            (Junta de Andalucía, Generalitat de Catalunya, País Vasco,
                            Galicia, Comunidad de Madrid, Junta de Castilla y León…) y filtra
                            por municipio, provincia, comunidad autónoma, presupuesto, plazo
                            abierto y código CPV. La IA expande automáticamente cada consulta
                            con sinónimos y CPV relacionados para que no se te escape ningún
                            anuncio relevante.
                        </p>
                        <h3 className="font-display text-xl md:text-2xl font-bold text-ink-950 mt-8 mb-3">
                            Seguridad y cumplimiento normativo
                        </h3>
                        <p>
                            La plataforma cumple los principios <strong>ISO 27001</strong> con
                            auditoría de eventos, cabeceras seguras, rate limiting, protección
                            anti-fuerza bruta y aislamiento por empresa. Los anexos firmados
                            generan archivos PDF/A con firma <strong>PAdES eIDAS</strong>{" "}
                            mediante AutoFirma, el cliente oficial del Gobierno de España, lo
                            que garantiza su validez jurídica plena ante la Administración
                            Pública y los tribunales.
                        </p>
                    </div>
                </div>
            </section>

            {/* CTA — Contact form */}
            <section
                id="precios"
                className="relative overflow-hidden bg-gradient-to-br from-ink-950 via-brand-900 to-brand-700 text-white"
            >
                <div className="absolute inset-0 bg-grid-brand opacity-20" />
                <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-cyan-400/20 blur-3xl float-slow" />
                <div className="max-w-7xl mx-auto px-6 py-24 lg:py-32 grid md:grid-cols-2 gap-16 items-start relative">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.7 }}
                    >
                        <div className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-300 mb-4">
                            — Solicitar demo
                        </div>
                        <h2 className="font-display text-4xl md:text-5xl tracking-tight font-black mb-5 leading-[1.05]">
                            Contacta y te abrimos una{" "}
                            <span className="text-cyan-300">demo en menos de 24 h</span>.
                        </h2>
                        <p className="text-lg text-white/70 leading-relaxed mb-8 max-w-xl font-medium">
                            Cuéntanos qué tipo de licitaciones presentas y prepararemos una
                            cuenta de prueba personalizada con tus pliegos.
                        </p>
                        <ul className="space-y-3">
                            {[
                                "Análisis IA con Claude Sonnet 4.5",
                                "Escandallo de costes editable",
                                "Generación automática de DEUC y anexos",
                                "Paquete ZIP listo para la PLCSP",
                                "Soporte en castellano",
                            ].map((l) => (
                                <li
                                    key={l}
                                    className="flex items-center gap-3 text-white bg-white/5 backdrop-blur rounded-xl px-4 py-3 border border-white/10"
                                >
                                    <div className="w-7 h-7 rounded-full bg-cyan-400/20 text-cyan-300 flex items-center justify-center shrink-0">
                                        <Check size={14} strokeWidth={3} />
                                    </div>
                                    <span className="text-base font-medium">{l}</span>
                                </li>
                            ))}
                        </ul>
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, x: 30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                    >
                        <ContactForm />
                    </motion.div>
                </div>
            </section>

            <footer className="border-t border-ink-100 bg-white">
                <div className="max-w-7xl mx-auto px-6 py-14">
                    <div className="grid md:grid-cols-4 gap-8 mb-10">
                        <div className="md:col-span-2">
                            <div className="flex items-center gap-2.5 mb-4">
                                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-brand-600 to-cyan-400 text-white flex items-center justify-center shadow-pop">
                                    <ShieldCheck size={18} strokeWidth={2.4} />
                                </div>
                                <span className="font-display text-xl font-extrabold tracking-tight text-ink-950">
                                    LicitaIA
                                </span>
                            </div>
                            <p className="text-sm text-ink-500 leading-relaxed max-w-md font-medium">
                                Software de inteligencia artificial para licitaciones públicas
                                en España. Análisis IA de pliegos, escandallo, anexos LCSP y
                                paquete listo para la PLCSP. Conforme a la Ley 9/2017 de
                                Contratos del Sector Público.
                            </p>
                        </div>
                        <div>
                            <div className="text-xs font-bold uppercase tracking-[0.18em] text-ink-400 mb-4">
                                Producto
                            </div>
                            <ul className="space-y-2 text-sm">
                                <li>
                                    <a
                                        href="#producto"
                                        className="text-ink-600 hover:text-brand-600 font-medium transition-colors"
                                    >
                                        Análisis IA de pliegos
                                    </a>
                                </li>
                                <li>
                                    <a
                                        href="#plcsp"
                                        className="text-ink-600 hover:text-brand-600 font-medium transition-colors"
                                    >
                                        Buscador PLCSP semántico
                                    </a>
                                </li>
                                <li>
                                    <a
                                        href="#equipos"
                                        className="text-ink-600 hover:text-brand-600 font-medium transition-colors"
                                    >
                                        Cuentas multi-usuario
                                    </a>
                                </li>
                                <li>
                                    <a
                                        href="#proceso"
                                        className="text-ink-600 hover:text-brand-600 font-medium transition-colors"
                                    >
                                        Cómo funciona
                                    </a>
                                </li>
                            </ul>
                        </div>
                        <div>
                            <div className="text-xs font-bold uppercase tracking-[0.18em] text-ink-400 mb-4">
                                Recursos
                            </div>
                            <ul className="space-y-2 text-sm">
                                <li>
                                    <a
                                        href="#faq"
                                        className="text-ink-600 hover:text-brand-600 font-medium transition-colors"
                                    >
                                        Preguntas frecuentes
                                    </a>
                                </li>
                                <li>
                                    <a
                                        href="#precios"
                                        className="text-ink-600 hover:text-brand-600 font-medium transition-colors"
                                    >
                                        Solicitar demo
                                    </a>
                                </li>
                                <li>
                                    <a
                                        href="mailto:hola@ailicitaurcal.com"
                                        className="text-ink-600 hover:text-brand-600 font-medium transition-colors"
                                    >
                                        hola@ailicitaurcal.com
                                    </a>
                                </li>
                                <li>
                                    <Link
                                        to="/login"
                                        className="text-ink-600 hover:text-brand-600 font-medium transition-colors"
                                    >
                                        Acceder
                                    </Link>
                                </li>
                            </ul>
                        </div>
                    </div>
                    <div className="border-t border-ink-100 pt-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                        <div className="text-sm text-ink-500 font-medium">
                            LicitaIA © {new Date().getFullYear()} — IA para contratación
                            pública española
                        </div>
                        <div className="text-xs text-ink-400 font-semibold uppercase tracking-wider">
                            Ley 9/2017 LCSP · ISO 27001 · PAdES eIDAS · AutoFirma
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}

// inline icon
function Plus(props) {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={props.strokeWidth || 2}
            strokeLinecap="round"
            strokeLinejoin="round"
            width={props.size || 20}
            height={props.size || 20}
        >
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
    );
}

function FaqItem({ q, a }) {
    const [open, setOpen] = useState(false);
    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.4 }}
            className="bg-white rounded-2xl border border-ink-100 hover:border-brand-200 shadow-pop transition-all overflow-hidden"
        >
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center justify-between gap-4 text-left px-6 py-5 hover:bg-brand-50/30 transition-colors"
                aria-expanded={open}
                data-testid={`faq-toggle-${q.slice(0, 20).toLowerCase().replace(/\s+/g, "-")}`}
            >
                <span className="font-display font-bold text-ink-950 text-base md:text-lg">
                    {q}
                </span>
                <motion.span
                    animate={{ rotate: open ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                    className="shrink-0 w-9 h-9 rounded-full bg-brand-50 text-brand-600 flex items-center justify-center"
                >
                    <ChevronDown size={16} strokeWidth={2.5} />
                </motion.span>
            </button>
            <motion.div
                initial={false}
                animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                className="overflow-hidden"
            >
                <div className="px-6 pb-5 text-ink-600 leading-relaxed text-sm md:text-base">
                    {a}
                </div>
            </motion.div>
        </motion.div>
    );
}

function ContactForm() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        company: "",
        message: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState(null);

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const onSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        if (!form.name.trim() || !form.email.trim()) {
            setError("Nombre y email son obligatorios");
            return;
        }
        setSubmitting(true);
        try {
            await submitContact(form);
            setSent(true);
            toast.success("¡Gracias! Te contactaremos en menos de 24 horas.");
        } catch (err) {
            const msg =
                err?.response?.data?.detail || "No se pudo enviar. Inténtalo de nuevo.";
            setError(msg);
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    if (sent) {
        return (
            <div
                className="bg-white/95 backdrop-blur-lg rounded-3xl p-10 text-center shadow-pop-lg"
                data-testid="contact-form-success"
            >
                <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-emerald-500 to-cyan-500 text-white flex items-center justify-center mb-5 shadow-pop">
                    <CheckCircle2 size={32} />
                </div>
                <div className="font-display text-2xl font-black text-ink-950 mb-2">
                    Solicitud recibida
                </div>
                <p className="text-base text-ink-500 leading-relaxed font-medium">
                    Te contactaremos a <span className="font-bold text-ink-950">{form.email}</span>{" "}
                    en menos de <span className="font-bold text-brand-600">24 horas</span> con
                    los próximos pasos.
                </p>
            </div>
        );
    }

    return (
        <form
            onSubmit={onSubmit}
            className="bg-white/95 backdrop-blur-lg rounded-3xl p-7 md:p-8 shadow-pop-lg space-y-4"
            data-testid="contact-form"
        >
            <div className="flex items-center gap-2 mb-1">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-brand-600 to-cyan-400 text-white flex items-center justify-center shadow-pop">
                    <Sparkles size={16} />
                </div>
                <div>
                    <div className="font-display text-lg font-black text-ink-950 leading-tight">
                        Solicita tu demo
                    </div>
                    <div className="text-xs font-bold uppercase tracking-wider text-ink-400">
                        Te respondemos en &lt; 24 h
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                    <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-400 block mb-1.5">
                        Nombre *
                    </label>
                    <Input
                        value={form.name}
                        onChange={update("name")}
                        placeholder="Tu nombre"
                        className="rounded-xl border-ink-100 h-11 font-medium text-ink-950"
                        data-testid="contact-name"
                        required
                    />
                </div>
                <div>
                    <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-400 block mb-1.5">
                        Email *
                    </label>
                    <Input
                        type="email"
                        value={form.email}
                        onChange={update("email")}
                        placeholder="tucorreo@empresa.es"
                        className="rounded-xl border-ink-100 h-11 font-medium text-ink-950"
                        data-testid="contact-email"
                        required
                    />
                </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                    <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-400 block mb-1.5">
                        Teléfono
                    </label>
                    <Input
                        value={form.phone}
                        onChange={update("phone")}
                        placeholder="+34 600 000 000"
                        className="rounded-xl border-ink-100 h-11 font-medium text-ink-950"
                        data-testid="contact-phone"
                    />
                </div>
                <div>
                    <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-400 block mb-1.5">
                        Empresa
                    </label>
                    <Input
                        value={form.company}
                        onChange={update("company")}
                        placeholder="Nombre de tu empresa"
                        className="rounded-xl border-ink-100 h-11 font-medium text-ink-950"
                        data-testid="contact-company"
                    />
                </div>
            </div>
            <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-400 block mb-1.5">
                    Cuéntanos brevemente
                </label>
                <Textarea
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Qué tipo de licitaciones presentas, volumen mensual, etc."
                    rows={4}
                    className="rounded-xl border-ink-100 font-medium text-ink-950 resize-y"
                    data-testid="contact-message"
                />
            </div>

            {error && (
                <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-800 p-3 rounded-xl text-sm">
                    <AlertCircle size={15} className="mt-0.5 shrink-0" />
                    <span>{error}</span>
                </div>
            )}

            <Button
                type="submit"
                disabled={submitting}
                className="w-full rounded-full bg-brand-600 hover:bg-brand-700 text-white h-14 text-base font-bold shadow-pop-lg hover:scale-[1.02] transition-all disabled:opacity-60"
                data-testid="contact-submit"
            >
                {submitting ? (
                    <>
                        <Loader2 size={16} className="animate-spin mr-2" />
                        Enviando…
                    </>
                ) : (
                    <>
                        <Sparkles size={16} className="mr-2" />
                        Solicitar demo gratuita
                        <ArrowRight size={16} className="ml-2" />
                    </>
                )}
            </Button>
            <p className="text-xs text-ink-400 font-medium text-center">
                Sin compromiso. Tus datos quedan protegidos y no se comparten con terceros.
            </p>
        </form>
    );
}
